import React, { memo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  useTheme,
  alpha,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Assessment as AssessmentIcon,
  FitnessCenter as FitnessIcon,
  Restaurant as NutritionIcon,
  DirectionsRun as MovementIcon,
  Science as MetabolicIcon,
  MedicalServices as AlliedHealthIcon,
  MonitorHeart as InbodyIcon,
  Assignment as IntakeIcon,
  LocalHospital as HealthConsultIcon,
  Description as PreHealthIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AssessmentCardProps, AssessmentCategory, PreAssessmentType } from './types';

const getAssessmentIcon = (category?: AssessmentCategory | PreAssessmentType) => {
  const iconProps = { fontSize: 'medium' as const };
  
  switch (category) {
    case 'Inbody':
      return <InbodyIcon {...iconProps} />;
    case 'Movement':
      return <MovementIcon {...iconProps} />;
    case 'Metabolic':
      return <MetabolicIcon {...iconProps} />;
    case 'Allied Health':
      return <AlliedHealthIcon {...iconProps} />;
    case 'Nutrition':
      return <NutritionIcon {...iconProps} />;
    case 'Training':
      return <FitnessIcon {...iconProps} />;
    case 'Pre-Health Consult':
      return <PreHealthIcon {...iconProps} />;
    case 'Health Consult':
      return <HealthConsultIcon {...iconProps} />;
    case 'Intake Form':
      return <IntakeIcon {...iconProps} />;
    default:
      return <AssessmentIcon {...iconProps} />;
  }
};

export const AssessmentCard = memo(({
  title,
  isSubmitted,
  onClick,
  isClickable = false,
  category,
  programmeType,
  isLoading = false,
  badgeLabel,
}: AssessmentCardProps) => {
  const theme = useTheme();
  const canClick = isClickable && isSubmitted && onClick;

  const cardVariants = {
    initial: {
      scale: 1,
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    hover: {
      scale: 1.02,
      boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
      transition: {
        type: 'spring' as const,
        stiffness: 300,
        damping: 20,
      },
    },
    tap: {
      scale: 0.98,
    },
  };

  const iconVariants = {
    initial: {
      rotate: 0,
    },
    hover: {
      rotate: isSubmitted ? 360 : 0,
      transition: {
        duration: 0.6,
        ease: 'easeInOut' as const,
      },
    },
  };

  const getProgrammeColor = () => {
    if (!isSubmitted) return theme.palette.grey[300];
    
    if (programmeType === 'Foundation') {
      return theme.palette.info.main;
    } else if (programmeType === 'Ignition') {
      return theme.palette.error.main;
    }
    
    return theme.palette.success.main;
  };

  const getBackgroundGradient = () => {
    const color = getProgrammeColor();
    
    if (!isSubmitted) {
      return `linear-gradient(135deg, ${alpha(theme.palette.grey[200], 0.3)} 0%, ${alpha(theme.palette.grey[100], 0.1)} 100%)`;
    }
    
    return `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`;
  };

  if (isLoading) {
    return (
      <Card sx={{ height: 120 }}>
        <CardContent>
          <Skeleton variant="circular" width={24} height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="60%" />
        </CardContent>
      </Card>
    );
  }

  const cardContent = (
    <motion.div
      variants={cardVariants}
      initial="initial"
      whileHover={canClick ? "hover" : "initial"}
      whileTap={canClick ? "tap" : "initial"}
      style={{ height: '100%' }}
    >
      <Card
        sx={{
          height: 120,
          cursor: canClick ? 'pointer' : 'default',
          border: `2px solid ${isSubmitted ? alpha(getProgrammeColor(), 0.3) : theme.palette.grey[300]}`,
          background: getBackgroundGradient(),
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at top right, ${alpha(getProgrammeColor(), 0.1)}, transparent)`,
            opacity: isSubmitted ? 1 : 0,
            transition: 'opacity 0.3s ease',
          },
        }}
        onClick={canClick ? onClick : undefined}
      >
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <motion.div variants={iconVariants}>
              {getAssessmentIcon(category)}
            </motion.div>
            <Typography variant="subtitle2" fontWeight={600}>
              {title}
            </Typography>
            {badgeLabel && (
              <Chip
                label={badgeLabel}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
          
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography
              variant="caption"
              color={isSubmitted ? 'success.main' : 'text.secondary'}
              sx={{ fontWeight: isSubmitted ? 600 : 400 }}
            >
              {isSubmitted ? 'Submitted' : 'Not Submitted'}
            </Typography>
            
            <motion.div
              animate={{
                scale: isSubmitted ? [1, 1.2, 1] : 1,
              }}
              transition={{
                duration: 0.5,
                times: [0, 0.5, 1],
              }}
            >
              {isSubmitted ? (
                <CheckCircleIcon
                  sx={{
                    color: getProgrammeColor(),
                    fontSize: 20,
                  }}
                />
              ) : (
                <RadioButtonUncheckedIcon
                  sx={{
                    color: theme.palette.grey[400],
                    fontSize: 20,
                  }}
                />
              )}
            </motion.div>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  if (canClick) {
    return (
      <Tooltip title="Click to view details" placement="top" arrow>
        {cardContent}
      </Tooltip>
    );
  }

  return cardContent;
});

AssessmentCard.displayName = 'AssessmentCard';
